import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import clientDashboardService from '@services/clientDashboardService';
import { useAuth } from '@contexts/AuthContext';

interface ProfileData {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  address?: string | { street?: string; city?: string; county?: string; postalCode?: string; country?: string; } | null;
}

interface Consents {
  gdpr: boolean;
  marketing: boolean;
  terms: boolean;
}

const ClientProfile: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [consents, setConsents] = useState<Consents>({
    gdpr: false,
    marketing: false,
    terms: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [profileData, consentsData] = await Promise.all([
        clientDashboardService.getProfile(),
        clientDashboardService.getConsents()
      ]);
      
      // Convert address to string if it's an object
      const normalizedProfile = {
        ...profileData,
        address: typeof profileData.address === 'object' && profileData.address !== null
          ? `${profileData.address.street || ''}, ${profileData.address.city || ''}, ${profileData.address.county || ''}`.trim()
          : profileData.address || ''
      };
      
      setProfile(normalizedProfile as ProfileData);
      
      // Handle consents - folosește formatul corect din backend
      setConsents({
        gdpr: (consentsData as any)?.gdprConsent || false,
        marketing: (consentsData as any)?.communicationPreferences?.email || false,
        terms: true // Terms sunt întotdeauna true (obligatorii)
      });
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Eroare la incarcarea profilului');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    try {
      setSaving(true);
      setError(null);
      
      // Convert string address back to object for API
      const addressForAPI = typeof profile.address === 'string' && profile.address
        ? { street: profile.address, city: '', county: '', postalCode: '', country: '' }
        : null;
      
      await clientDashboardService.updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        address: addressForAPI
      } as any);

      setSnackbar({ open: true, message: 'Profil actualizat cu succes!', severity: 'success' });
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Eroare la salvarea profilului');
    } finally {
      setSaving(false);
    }
  };

  const handleConsentChange = async (type: keyof Consents, value: boolean) => {
    try {
      if (type === 'gdpr') {
        // Pentru GDPR, folosește formatul corect
        await clientDashboardService.updateConsents({
          gdprConsent: value,
          communicationPreferences: {
            email: consents.marketing, // Păstrează marketing pentru email
            sms: false,
            whatsapp: false
          }
        });
      } else if (type === 'marketing') {
        // Pentru marketing, folosește formatul corect
        await clientDashboardService.updateConsents({
          gdprConsent: consents.gdpr, // Păstrează GDPR
          communicationPreferences: {
            email: value, // Marketing = email
            sms: false,
            whatsapp: false
          }
        });
      }

      setConsents(prev => ({ ...prev, [type]: value }));
      setSnackbar({ open: true, message: 'Consimtamant actualizat!', severity: 'success' });
      
      // Reîncarcă datele pentru a fi sigur
      await loadProfileData();
    } catch (err) {
      console.error('Error updating consent:', err);
      setError('Eroare la actualizarea consimtamantului');
      // Revert the change
      setConsents(prev => ({ ...prev, [type]: !value }));
    }
  };

  const handleExportData = async () => {
    try {
      const response = await clientDashboardService.startExport({
        format: 'json',
        entities: ['profile', 'animals', 'appointments', 'documents', 'messages', 'invoices', 'notifications'],
      });

      setSnackbar({ 
        open: true, 
        message: response.export_id
          ? 'Exportul a fost pornit. Il poti urmari din istoricul de exporturi.'
          : 'Exportul a fost generat si este disponibil pentru descarcare.', 
        severity: 'success' 
      });
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Error requesting data export:', err);
      setError('Eroare la exportul datelor');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteDialogOpen(false);
    setError('Stergerea contului nu este disponibila in flow-ul owner actual.');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          ⚙️ Setarile Mele
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestioneaza datele tale personale si preferintele de confidentialitate
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PersonIcon />
                Informatii Personale
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Prenume"
                    value={profile?.firstName || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, firstName: e.target.value } : null)}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nume"
                    value={profile?.lastName || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, lastName: e.target.value } : null)}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile?.email || ''}
                    disabled
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Email-ul nu poate fi modificat"
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={profile?.phone || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, phone: e.target.value } : null)}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Adresa"
                    multiline
                    rows={2}
                    value={profile?.address || ''}
                    onChange={(e) => setProfile(prev => prev ? { ...prev, address: e.target.value } : null)}
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 2 }} />
                    }}
                  />
                </Grid>

                <Grid size={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={saving}
                    sx={{ textTransform: 'none', px: 4 }}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Salveaza Modificarile'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Privacy & Consents */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon />
                Confidentialitate & GDPR
              </Typography>

              <List>
                <ListItem sx={{ px: 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={consents.gdpr}
                        onChange={(e) => handleConsentChange('gdpr', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Consimtamant GDPR
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Accepta prelucrarea datelor personale
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </ListItem>

                <Divider sx={{ my: 1 }} />

                <ListItem sx={{ px: 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={consents.marketing}
                        onChange={(e) => handleConsentChange('marketing', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Marketing
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Primeste oferte si noutati
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </ListItem>

                <Divider sx={{ my: 1 }} />

                <ListItem sx={{ px: 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={consents.terms}
                        disabled
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Termeni & Conditii
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Acceptare obligatorie
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningIcon color="warning" />
                Zona Periculoasa
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => setExportDialogOpen(true)}
                sx={{ mb: 2, textTransform: 'none' }}
              >
                Exporta datele mele
              </Button>

              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Sterge contul
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Data Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Exporta datele tale</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Vei primi un email cu toate datele tale personale in format JSON.
          </Alert>
          <Typography variant="body2">
            Exportul include:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Informatii profil" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Animalele tale" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Programari" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Documente" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Istoric plati" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Anuleaza</Button>
          <Button onClick={handleExportData} variant="contained" startIcon={<DownloadIcon />}>
            Exporta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Stergere cont indisponibila</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Fluxul de stergere cont nu are backend dedicat in aceasta versiune.
          </Alert>
          <Typography variant="body2" gutterBottom>
            In prezent poti:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><WarningIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText primary="actualiza profilul si consimtamantele" />
            </ListItem>
            <ListItem>
              <ListItemIcon><WarningIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText primary="porni exportul datelor din ecranul curent" />
            </ListItem>
            <ListItem>
              <ListItemIcon><WarningIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText primary="gestiona programari, documente, mesaje si facturi din modulele dedicate" />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
            Cererea de stergere cont trebuie tratata in afara produsului actual.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuleaza</Button>
          <Button onClick={handleDeleteAccount} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Inchide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClientProfile;

